import React from 'react';

import {
  Dropdown,
  Badge,
  Button,
  Toggle,
  Popup,
  copyToClipboard,
} from '@components';
import {
  useAuth,
  useNotifications,
  useTheme,
  NotificationsFeed,
} from '@shared';
import { getContactString } from '@molecules';
import TimezoneDropdown from './TimezoneDropdown';

type UserBarProps = {};

const UserAvatar: React.FC = () => {
  const { user } = useAuth();
  if (!user) return null;

  const { picture, nickname } = user;
  const element = picture ? (
    <img src={picture} className="h-full w-full" alt="" />
  ) : (
    <Badge shrink rounded text={nickname || ''} />
  );

  return (
    <div className="flex flex-row items-center justify-center gap-x-sm">
      <div className="font-medium text-small">{nickname}</div>
      <div className="aspect-square h-[4rem] overflow-hidden rounded-full">
        {element}
      </div>
    </div>
  );
};

const OrgSelector: React.FC = () => {
  const { orgs, organization_id, setSelectedOrg } = useAuth();
  if (!orgs || orgs.length <= 1) return null;

  return (
    <Dropdown
      value={organization_id || ''}
      onChange={value => setSelectedOrg(value)}
      options={orgs
        .map(o => ({ value: o.id, label: o.name || '' }))
        .filter(o => o.value)}
    />
  );
};

const UserBar: React.FC<UserBarProps> = () => {
  const { user, logout, token } = useAuth();
  const { theme, onToggle } = useTheme();
  const isDark = theme === 'dark';
  const notifications = useNotifications();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div className="ml-auto flex h-[4rem] items-center justify-center gap-x-sm">
      <OrgSelector />
      <NotificationsFeed {...notifications} />
      <Popup
        trigger={<UserAvatar />}
        popupClassName="flex flex-col items-center gap-y-md rounded-sm p-md"
      >
        <div>{getContactString(user, true)}</div>
        <Toggle
          value={isDark}
          onChange={onToggle}
          label="Theme"
          labelClass="text-small"
          transitionColor={false}
        />
        <div className="flex w-full flex-col gap-y-xs">
          <span className="text-small text-text">Display timezone</span>
          <TimezoneDropdown />
        </div>
        <Button
          icon="copy"
          className="w-full"
          onClick={() => copyToClipboard(token, 'Token copied!')}
        >
          Copy Token
        </Button>
        <Button type="danger" className="w-full" onClick={handleLogout}>
          Logout
        </Button>
      </Popup>
    </div>
  );
};

export default React.memo(UserBar) as typeof UserBar;
